import React, { useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  LayoutDashboard,
  Building2,
  UserCheck,
  MapPin,
  AlertTriangle,
  MessageCircle,
  Activity
} from 'lucide-react';
import { SecurityBoundary } from '@vehix/portal/lib/security';
import { logDataAccess } from '@vehix/portal/lib/security/audit';
import { SystemRole } from '@vehix/portal/types';
import { useAuth } from '@vehix/shared/admin/portal-bridge';
import { useAllTenants } from '@vehix/portal/hooks/useTenants';
import { useZones } from '../hooks/useZones';
import { usePipelines } from '../hooks/usePipelineOrchestrator';
import { useFeedbackStats } from '@vehix/portal/features/feedback/hooks/useFeedback';
import { useActivitySummary } from '../hooks/useUserActivityDashboard';
import { useUserApprovalsCount } from '../hooks/useUserApprovalsCount';
import { StatCard, type AttentionLevel } from '../components/dashboard/StatCard';

const today = () => new Date().toISOString().slice(0, 10);

const ONE_DAY_MS = 24 * 60 * 60 * 1000;

// eslint-disable-next-line max-lines-per-function
const AdminDashboardPage: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  // Audit page access on mount.
  useEffect(() => {
    if (user) {
      logDataAccess({
        resource: 'dashboard',
        action: 'view',
        metadata: { page: 'dashboard' }
      }).catch(() => {
        /* silent — audit logging should not surface errors */
      });
    }
  }, [user]);

  // === Data sources for the KPI strip ===
  const tenantsQuery = useAllTenants();
  const approvalsQuery = useUserApprovalsCount();
  const zonesQuery = useZones();
  const pipelinesQuery = usePipelines();
  const feedbackQuery = useFeedbackStats();
  const activityQuery = useActivitySummary({
    dateStart: today(),
    dateEnd: today(),
    granularity: 'day'
  });

  // === Derived metrics ===
  const tenantsCount = tenantsQuery.data?.length ?? 0;
  const pendingApprovals = approvalsQuery.data ?? 0;
  const zonesCount = zonesQuery.data?.length ?? 0;
  const openFeedback = feedbackQuery.data?.open_conversations ?? 0;

  const recentFailedPipelines = useMemo(() => {
    const pipelines = pipelinesQuery.data?.pipelines ?? [];
    const cutoff = Date.now() - ONE_DAY_MS;
    return pipelines.filter(p => {
      if (p.status !== 'failed') return false;
      if (!p.started_at) return false;
      return new Date(p.started_at).getTime() >= cutoff;
    }).length;
  }, [pipelinesQuery.data]);

  const dailyActiveUsers = useMemo(() => {
    const rows = activityQuery.data ?? [];
    if (rows.length === 0) return 0;
    // Each row already represents a unique-users count for one bucket — take the max
    // since rows can split by category but the user count is the same denominator.
    return Math.max(...rows.map(r => Number(r.unique_users)), 0);
  }, [activityQuery.data]);

  // === Attention levels ===
  const tenantsAttention: AttentionLevel = tenantsCount === 0 ? 'critical' : undefined;
  const approvalsAttention: AttentionLevel =
    pendingApprovals > 20 ? 'critical' : pendingApprovals > 5 ? 'warning' : undefined;
  const failuresAttention: AttentionLevel =
    recentFailedPipelines > 3 ? 'critical' : recentFailedPipelines > 0 ? 'warning' : undefined;
  const feedbackAttention: AttentionLevel = openFeedback > 10 ? 'warning' : undefined;
  const dauAttention: AttentionLevel = dailyActiveUsers === 0 ? 'critical' : undefined;

  return (
    <SecurityBoundary
      requiredSystemRole={SystemRole.SUPER_ADMIN}
      auditPageAccess='dashboard'
      tenantIsolation={false}
    >
      <div className='flex h-full w-full flex-col space-y-6 overflow-y-auto p-6'>
        {/* Header */}
        <div className='flex items-start gap-3 flex-shrink-0'>
          <LayoutDashboard className='h-6 w-6 text-primary mt-1' />
          <div>
            <h1 className='text-3xl font-bold tracking-tight'>Admin Dashboard</h1>
            <p className='text-muted-foreground text-sm'>
              At-a-glance health and activity across all tenants
            </p>
          </div>
        </div>

        {/* KPI strip — 6 widgets, click-through to relevant pages */}
        <div className='grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3'>
          <StatCard
            title='Tenants'
            value={tenantsCount}
            icon={Building2}
            loading={tenantsQuery.isLoading}
            attention={tenantsAttention}
            onClick={() => navigate('/setup/tenants')}
          />
          <StatCard
            title='Pending Approvals'
            value={pendingApprovals}
            icon={UserCheck}
            loading={approvalsQuery.isLoading}
            attention={approvalsAttention}
            onClick={() => navigate('/setup/users')}
          />
          <StatCard
            title='Active Zones'
            value={zonesCount}
            icon={MapPin}
            loading={zonesQuery.isLoading}
            onClick={() => navigate('/setup/zones')}
          />
          <StatCard
            title='Pipeline Failures'
            value={recentFailedPipelines}
            icon={AlertTriangle}
            loading={pipelinesQuery.isLoading}
            attention={failuresAttention}
            description='Last 24h'
            onClick={() => navigate('/workflow/pipelines')}
          />
          <StatCard
            title='Open Feedback'
            value={openFeedback}
            icon={MessageCircle}
            loading={feedbackQuery.isLoading}
            attention={feedbackAttention}
            onClick={() => navigate('/feedback')}
          />
          <StatCard
            title='Active Users Today'
            value={dailyActiveUsers}
            icon={Activity}
            loading={activityQuery.isLoading}
            attention={dauAttention}
            onClick={() => navigate('/analytics')}
          />
        </div>

        {/* Future widgets slot — leave empty until next iteration. Larger cards
            (recent failures table, feedback inbox preview, audit log feed,
            sparklines) will live here without restructuring the page. */}
        <section className='flex-1 min-h-0' aria-label='Additional dashboard widgets' />
      </div>
    </SecurityBoundary>
  );
};

export default AdminDashboardPage;
